<script setup>
import { computed, ref } from "vue";

import { isTouchDevice } from "common/utils/browser";
import CommonModal from "common/vue/components/CommonModal.vue";
import UseSvg from "../vue-cart/UseSvg.vue";
import ArtworkImage from "./ArtworkImage.vue";
import BackgroundToggle from "./common/BackgroundToggle.vue";
import MobileImageModal from "./common/MobileImageModal.vue";
import { useDtfStore } from "./store";
import { getArtworkSrc } from "./utils";

const props = defineProps({
  artwork: {
    type: Object,
    required: false,
    default: () => ({})
  },
  activeConfigSection: {
    type: String,
    required: true
  },
  withMade: {
    type: Boolean,
    default: false
  },
  shouldDisableSlider: {
    type: Boolean,
    default: false
  },
  sliderBeforeImage: {
    type: String,
    default: ""
  }
});

const store = useDtfStore();
const isStandardArtwork = computed(() => store.isStandardArtwork);
const showComparisonModal = ref(false);
const enabledButton = computed(
  () => isStandardArtwork.value && !props.artwork?.isLoading && props.artwork?.url
);

const showFullScreenButton = computed(
  () => (enabledButton.value || props.withMade) && isTouchDevice()
);

const showDesktopModal = computed(
  () => isStandardArtwork.value && showComparisonModal.value && props.artwork && !isTouchDevice()
);

const showMobileModal = computed(
  () => showComparisonModal.value && props.artwork && isTouchDevice()
);

const wrapperClass = computed(() => `js-modal-magnify-wrapper-${props.artwork?.id || "x"}`);
const artworkSrc = computed(() => getArtworkSrc(props.artwork));

function toggleComparisonModal() {
  showComparisonModal.value = !showComparisonModal.value;
}
</script>

<template>
  <button
    v-if="showFullScreenButton"
    class="artwork-full-screen__button"
    :class="{ 'artwork-full-screen__button--with-made': withMade }"
    :disabled="!enabledButton"
    type="button"
    @click="toggleComparisonModal"
  >
    <UseSvg class="artwork-full-screen__button-icon" icon="full-screen" />
    <span>Full Screen</span>
  </button>
  <CommonModal v-if="showDesktopModal" @on-close="toggleComparisonModal">
    <BackgroundToggle
      :model-value="store.backgroundType"
      @update:model-value="store.setBackgroundType($event)"
    />
    <div class="artwork-full-screen__artwork-image">
      <ArtworkImage
        :key="artwork.id"
        :artwork="artwork"
        fit-to-container
        :active-config-section="activeConfigSection"
      />
    </div>
  </CommonModal>
  <MobileImageModal
    v-if="showMobileModal"
    :src="shouldDisableSlider ? sliderBeforeImage : artworkSrc"
    :wrapper-class="wrapperClass"
    @close="toggleComparisonModal"
  />
</template>

<style lang="scss" scoped>
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.artwork-full-screen {
  &__button {
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    padding: 8px;
    gap: 8px;
    border-radius: 0px 0px 8px 8px;
    background: $crafterpay-gray-modern-100;
    color: $scorpion;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    top: -18px;
    position: relative;
    margin: 0px 15px;
    width: calc(100% - 20px);

    &--with-made {
      width: 100%;
      top: 0;
      margin: 0;
    }
  }

  &__button-icon {
    width: 24px;
    height: 24px;
  }

  &__artwork-image {
    padding-top: 35px;
    padding-left: 10px;

    @include with-desktop {
      padding-left: 0;
    }
  }
}
</style>
